import React, {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../layout/components/CustomButton";
import { RootState } from "../../../../redux/store";
import { Box, CircularProgress, styled } from "@mui/material";
import { setNextButtonClick } from "../../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { setHasSelectedLocation } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import {BookingCreationStep} from "../../roomRental/BookLocationPage";

const NextButton = styled(CustomButton)(({ theme }) => ({
  width: "100%",
  borderRadius: theme.shape.borderRadius,
  border: "none",
  "&:hover": {
    backgroundColor: theme.palette.secondary.contrastText,
    color: theme.palette.button.text,
    border: "none",
  },
  "&:focus": {
    outline: "none",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.button.text,
  },
  textTransform: "none",
}));

interface BookLocationNavigateNextActionProps {
  handleCall?: () => Promise<any>;
}

const ApartmentRentalNavigateNextAction: React.FC<
  BookLocationNavigateNextActionProps
> = ({ handleCall }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const step = useSelector((state: RootState) => state.apartment.step);
  const [loading, setLoading] = useState(false);
  const { nextEnabled } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  useEffect(() => {
    if (step !== undefined) {
      if (step !== BookingCreationStep.personalData) {
        dispatch(setHasSelectedLocation(true));
      }
    }
  }, [dispatch, step]);

  const isBookButtonVisible = step === 3; // Example condition for visibility
  const isProceedToPaymentButtonVisible = step === 4; // Example condition for visibility

  const handleNextClick = async () => {
    if (step === BookingCreationStep.bookingSummary && handleCall) {
      setLoading(true);
      await handleCall();
      setLoading(false);
      dispatch(setNextButtonClick());
    } else {
      dispatch(setNextButtonClick());
    };
    if (step === BookingCreationStep.bookingSummary) {
      dispatch(setHasSelectedLocation(false));
    }
  };

  const buttonText = t(
    step === 4
      ? "book_location.apartment_overview.reservation_request"
      : step === 2
      ? "book_location.apartment_overview.book_now"
      : isBookButtonVisible
      ? "book_location.confirmation.order_now"
      : isProceedToPaymentButtonVisible
      ? "book_location.confirmation.proceed_to_payment"
      : "book_location.next"
  );

  return (
    <Box sx={{ marginBottom: "-40px" }}>
      <NextButton
        key={`${true}`} // Fixed key (removed unnecessary negation)
        variant={true ? "outlined" : "contained"}
        disabled={!nextEnabled}
        onClick={handleNextClick}
        isButtonDark={true}
      >
        {loading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} color="inherit" />
            {t("book_location.apartment_overview.reservation_request")}
          </Box>
        ) : (
          buttonText
        )}
      </NextButton>
    </Box>
  );
};

export default ApartmentRentalNavigateNextAction;
